/** Client voor de tumedia-server. Later gegenereerd uit packages/api-contract/openapi.yaml. */

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
  }
}

export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(res.status, String(data.error ?? 'error'), typeof data.message === 'string' ? data.message : undefined);
  }
  return data as T;
}

export type MaxQuality = 'original' | 20000 | 8000 | 4000;

/** Wat een profiel mag (ingesteld door de beheerder). */
export interface Policy {
  liveTv: boolean;
  requests: boolean;
  requestApproval: boolean;
  addons: boolean;
  music: boolean;
  debrid?: boolean;
  usenet?: boolean;
  downloads?: boolean;
  skinLocked: boolean;
  layoutEdit: boolean;
  maxQuality: MaxQuality;
  maxAge?: number | null;
  template?: string;
}

export interface Account {
  id: number;
  username: string;
  title: string;
  thumb: string;
  /** plex = inloggen met Plex; local = e-mail en wachtwoord; both = e-mail-account met Plex gekoppeld */
  kind?: 'plex' | 'local' | 'both';
  mustChangePassword?: boolean;
}

export interface Profile {
  uuid: string;
  title: string;
  thumb: string;
  admin?: boolean;
  restricted: boolean;
  protected?: boolean;
  /** Profielen van een account zonder Plex */
  kid?: boolean;
  color?: string;
}

export interface Me {
  account: Account;
  profile: Profile | null;
  /** Nieuw via een uitnodiging: eerst de welkomst-wizard */
  onboarding?: boolean;
  /** Heeft het gekozen profiel toegang tot de Plex-bibliotheek? */
  plex?: boolean;
}

export interface Pin {
  id: number;
  code: string;
  expiresAt: string;
  linkUrl: string;
}

export type PinStatus = { status: 'waiting' } | ({ status: 'ok'; sessionToken: string } & Me);

export const api = {
  me: () => call<Me>('/api/auth/me'),
  createPin: () => call<Pin>('/api/auth/pin', { method: 'POST' }),
  checkPin: (id: number) => call<PinStatus>(`/api/auth/pin/${id}`),
  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  login: (email: string, password: string) => call<{ status: 'ok' } & Me>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  changePassword: (current: string, next: string) => call<{ ok: true }>('/api/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) }),

  profiles: () => call<{ profiles: Profile[]; local?: boolean; max?: number }>('/api/profiles'),
  addProfile: (title: string, kid: boolean) => call<{ profiles: Profile[] }>('/api/profiles', { method: 'POST', body: JSON.stringify({ title, kid }) }),
  updateProfile: (uuid: string, b: { title?: string; kid?: boolean; pin?: string | null; color?: string }) =>
    call<{ profiles: Profile[] }>(`/api/profiles/${encodeURIComponent(uuid)}`, { method: 'PATCH', body: JSON.stringify(b) }),
  deleteProfile: (uuid: string) => call<{ profiles: Profile[] }>(`/api/profiles/${encodeURIComponent(uuid)}`, { method: 'DELETE' }),
  plexLink: () => call<{ linked: boolean; username: string | null }>('/api/account/plex'),
  plexLinkPin: () => call<Pin>('/api/account/plex/pin', { method: 'POST' }),
  plexLinkCheck: (id: number) => call<{ status: 'waiting' } | { status: 'ok'; access: boolean; message: string }>(`/api/account/plex/pin/${id}`),
  plexUnlink: () => call<{ linked: false }>('/api/account/plex', { method: 'DELETE' }),
  selectProfile: (uuid: string, pin?: string) =>
    call<{ profile: Profile }>(`/api/profiles/${encodeURIComponent(uuid)}/select`, {
      method: 'POST',
      body: JSON.stringify(pin ? { pin } : {}),
    }),

  plexConnection: () => call<{ url: string; token: string }>('/api/plex/connection'),
  theme: (id: string) => call<Theme>(`/api/themes/${encodeURIComponent(id)}`),
};

// ---------- IPTV ----------

export interface IptvAccount {
  id: string;
  name: string;
  kind?: 'xtream' | 'm3u' | 'stalker';
  serverUrl: string;
  username: string;
  providerMaxConnections: number;
  maxConnectionsOverride: number | null;
  maxConnections: number;
  disabledProfiles: string[];
  enabledProfiles: string[];
  status: string | null;
  expiresAt: number | null;
  hiddenCategories: string[];
  hiddenStreams: number[];
  epgUrl: string | null;
  enabledForProfile?: boolean;
}

export interface IptvChannel {
  accountId: string;
  streamId: number;
  name: string;
  logo: string | null;
  epgId: string | null;
  categoryId: string;
  categoryName: string;
  catchup: boolean;
  favorite: boolean;
  /** Event-zender die nu (bijna) een wedstrijd uitzendt */
  eventTitle?: string;
  eventLive?: boolean;
}

export interface EpgProgramme {
  start: number;
  stop: number;
  title: string;
  desc?: string;
}

export interface SportEvent {
  id: string;
  sport: string;
  title: string;
  competition?: string;
  /** Id van de competitie (zie /api/sport/leagues) */
  league?: string;
  /** Gekoppeld aan het echte schema: clublogo's, stand en minuut */
  match?: {
    id: string;
    state: 'pre' | 'in' | 'post';
    detail: string;
    name: string;
    start: number;
    home: { name: string; logo: string | null; score: string | null } | null;
    away: { name: string; logo: string | null; score: string | null } | null;
  };
  rerun?: boolean;
  eventChannel?: boolean;
  session?: string;
  detail?: string;
  start: number;
  stop: number;
  live: boolean;
  nl: boolean;
  favorite: boolean;
  channels: { accountId: string; streamId: number; name: string; logo: string | null; nl: boolean; catchup: boolean }[];
}

export interface League {
  id: string;
  name: string;
  sport: string;
  sportName: string;
  abbr: string;
  color: string;
  nl: boolean;
  default: boolean;
  badge: string | null;
  logo: string | null;
}

export const sportApi = {
  leagues: () => call<{ leagues: League[] }>('/api/sport/leagues'),
};

export const iptv = {
  accounts: () => call<{ accounts: IptvAccount[] }>('/api/iptv/accounts'),
  addOther: (b: { kind: 'm3u' | 'stalker'; name?: string; url?: string; portalUrl?: string; mac?: string; maxConnectionsOverride?: number }) =>
    call<{ account: IptvAccount }>('/api/iptv/accounts/other', { method: 'POST', body: JSON.stringify(b) }),
  addAccount: (b: { name?: string; serverUrl: string; username: string; password: string; maxConnectionsOverride?: number }) =>
    call<{ account: IptvAccount }>('/api/iptv/accounts', { method: 'POST', body: JSON.stringify(b) }),
  updateAccount: (id: string, b: Partial<{ name: string; maxConnectionsOverride: number | null; disabledProfiles: string[]; enabledProfiles: string[]; password: string; hiddenCategories: string[]; hiddenStreams: number[]; epgUrl: string | null }>) =>
    call<{ account: IptvAccount }>(`/api/iptv/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
  accountChannels: (id: string) =>
    call<{ categories: { id: string; name: string; count: number; hidden: boolean }[]; channels: { streamId: number; name: string; categoryId: string; hidden: boolean }[] }>(`/api/iptv/accounts/${id}/channels`),
  deleteAccount: (id: string) => call<{ ok: true }>(`/api/iptv/accounts/${id}`, { method: 'DELETE' }),
  channels: () => call<{ categories: { id: string; accountId: string; name: string }[]; channels: IptvChannel[]; errors: { accountId: string; message: string }[]; recent?: string[] }>('/api/iptv/channels'),
  setFavorite: (accountId: string, streamId: number, favorite: boolean) =>
    call<{ ok: true }>('/api/iptv/favorites', { method: 'POST', body: JSON.stringify({ accountId, streamId, favorite }) }),
  epg: (accountId: string, streams: number[], from: number, to: number) =>
    call<{ from: number; to: number; programmes: Record<string, EpgProgramme[]> }>(
      `/api/iptv/epg?accountId=${encodeURIComponent(accountId)}&streams=${streams.join(',')}&from=${from}&to=${to}`,
    ),
  sport: (hours = 24) =>
    call<{
      events: SportEvent[];
      others: { title: string; subTitle?: string; start: number; stop: number; channel: { accountId: string; streamId: number; name: string; logo: string | null; catchup: boolean } }[];
      favoriteClubs: string[];
      errors: { accountId: string; message: string }[];
    }>(`/api/iptv/sport?hours=${hours}`),
  setFavoriteClubs: (clubs: string[]) => call<{ favoriteClubs: string[] }>('/api/iptv/favorite-clubs', { method: 'PUT', body: JSON.stringify({ clubs }) }),
  play: (accountId: string, streamId: number, opts: { slot?: string; start?: number; end?: number; fmp4?: boolean; transcode?: boolean } = {}) =>
    call<{ sessionId: string; url: string; fallback?: { accountId: string; accountName: string; streamId: number } | null }>('/api/iptv/play', { method: 'POST', body: JSON.stringify({ accountId, streamId, ...opts }) }),
  stop: (sid: string) => fetch(`/api/iptv/hls/${sid}/stop`, { method: 'POST', credentials: 'same-origin', keepalive: true }).catch(() => undefined),
};

// ---------- Seerr ----------

export type MediaStatus = 'none' | 'requested' | 'processing' | 'partial' | 'available';

export interface SeerrMedia {
  tmdbId: number;
  mediaType: 'movie' | 'tv';
  title: string;
  year: number | null;
  poster: string | null;
  backdrop: string | null;
  overview: string;
  rating: number | null;
  status: MediaStatus;
}

export interface SeerrRequest {
  id: number;
  approval: 'pending' | 'approved' | 'declined';
  createdAt: string;
  seasons: number[];
  media: SeerrMedia | null;
}

export const seerrApi = {
  status: () => call<{ configured: boolean; url: string | null; canManage: boolean; canRequest: boolean }>('/api/seerr/status'),
  saveConfig: (url: string, apiKey: string) => call<{ configured: boolean; url: string | null }>('/api/seerr/config', { method: 'PUT', body: JSON.stringify({ url, apiKey }) }),
  clearConfig: () => call<{ configured: boolean }>('/api/seerr/config', { method: 'DELETE' }),
  search: (q: string) => call<{ results: SeerrMedia[] }>(`/api/seerr/search?q=${encodeURIComponent(q)}`),
  discover: (kind: 'trending' | 'movies' | 'tv' = 'trending') => call<{ results: SeerrMedia[] }>(`/api/seerr/discover?kind=${kind}`),
  tv: (id: number) => call<{ title: string; seasons: { number: number; name: string; episodes: number; status: MediaStatus }[] }>(`/api/seerr/tv/${id}`),
  request: (mediaType: 'movie' | 'tv', tmdbId: number, seasons?: number[], info?: { title?: string; poster?: string | null; profileId?: number }) =>
    call<{ ok: true; asUser?: boolean; already?: boolean; pending?: boolean; profileApplied?: boolean }>('/api/seerr/request', { method: 'POST', body: JSON.stringify({ mediaType, tmdbId, seasons, title: info?.title, poster: info?.poster ?? undefined, profileId: info?.profileId }) }),
  /** Kwaliteitsprofielen waaruit je bij deze film kunt kiezen (leeg = niets te kiezen) */
  movieProfiles: (tmdbId: number) => call<{ profiles: { id: number; name: string }[]; suggested?: number; defaultId?: number | null }>(`/api/seerr/movie-profiles?tmdb=${tmdbId}`),
  radarr: () => call<{ servers: { id: number; name: string; isDefault: boolean; is4k: boolean; defaultProfileId: number; profiles: { id: number; name: string }[] }[]; config: { serverId: number; ids: number[]; kidsId?: number } | null }>('/api/seerr/radarr'),
  saveMovieProfiles: (b: { serverId: number; ids: number[]; kidsId: number | null }) => call<{ config: { serverId: number; ids: number[]; kidsId?: number } | null }>('/api/seerr/movie-profiles', { method: 'PUT', body: JSON.stringify(b) }),
  requests: () => call<{ requests: SeerrRequest[]; linked: boolean }>('/api/seerr/requests'),
};

// ---------- Metadata en koppelingen ----------

export type Provider = 'tmdb' | 'fanart' | 'mdblist' | 'traktClientId' | 'traktClientSecret' | 'simklClientId' | 'opensubtitles' | 'opensubtitlesUser' | 'opensubtitlesPass';

export interface Ratings {
  imdb?: number;
  tomatoes?: number;
  popcorn?: number;
  metacritic?: number;
  letterboxd?: number;
  trakt?: number;
  tmdb?: number;
}

export interface MiniItem {
  type: 'movie' | 'tv';
  tmdbId: number;
  title: string;
  year: number | null;
  poster: string | null;
  backdrop: string | null;  /** Eigen tweede regel (bijv. de rol van een acteur); anders het jaar */
  sub?: string;
}

export interface Meta {
  type: 'movie' | 'tv';
  tmdbId: number;
  title: string;
  tagline: string | null;
  overview: string | null;
  year: number | null;
  logo: string | null;
  backdrop: string | null;
  poster: string | null;
  ratings: Ratings;
  trailer: { key: string; name: string } | null;
  watchProviders: { name: string; logo: string | null }[];
  collection: { name: string; parts: MiniItem[] } | null;
  recommendations: MiniItem[];
  imdbId: string | null;
  cast?: { id: number; name: string; character: string | null; photo: string | null }[];
  disc?: string | null;
  clearart?: string | null;
  directors?: { id: number; name: string; job: string }[];
  status?: string | null;
  nextEpisode?: { season: number; episode: number; title: string; date: string } | null;
  seasons?: { number: number; name: string; episodes: number; airDate: string | null; poster: string | null }[];
  genres?: string[];
  runtime?: number | null;
  certification?: string | null;
  seasonCount?: number | null;
  /** IMDb Parents Guide (null = nog niet opgehaald) */
  parentsGuide?: GuideItem[] | null;
}

export type ShelfKind = '4k' | 'dv' | 'hdr10p' | 'atmos' | 'remux' | 'dtsx';

export interface Review { source: 'trakt' | 'tmdb'; author: string; rating: number | null; text: string; likes: number; date: string | null; full: boolean }

export const titleApi = {
  color: (type: 'movie' | 'tv', tmdbId: number) => call<{ color: { bg: string; glow: string } | null }>(`/api/meta/${type}/${tmdbId}/color`),
  reviews: (type: 'movie' | 'tv', tmdbId: number) => call<{ reviews: Review[] }>(`/api/meta/${type}/${tmdbId}/reviews`),
  shelf: (kind: ShelfKind, limit: number) => call<{ keys: string[] }>(`/api/library/shelf?kind=${kind}&limit=${limit}`),
};

export interface GuideItem {
  id: 'NUDITY' | 'VIOLENCE' | 'PROFANITY' | 'ALCOHOL' | 'FRIGHTENING';
  label: string;
  level: 'NONE' | 'MILD' | 'MODERATE' | 'SEVERE';
  levelLabel: string;
}

export const guideApi = {
  get: (q: { imdb?: string; tmdb?: number; type?: string }) => {
    const p = new URLSearchParams();
    if (q.imdb) p.set('imdb', q.imdb);
    if (q.tmdb) p.set('tmdb', String(q.tmdb));
    if (q.type) p.set('type', q.type);
    return call<{ certification: string | null; guide: GuideItem[] }>(`/api/parents-guide?${p}`);
  },
};

export interface PersonCredit {
  type: 'movie' | 'tv';
  tmdbId: number;
  title: string;
  year: number | null;
  date: string | null;
  poster: string | null;
  backdrop: string | null;
  role: string | null;
  job: 'cast' | 'directing' | 'writing' | 'production' | 'other';
  popularity: number;
  votes: number;
}

export interface Person {
  id: number;
  name: string;
  biography: string | null;
  birthday: string | null;
  deathday: string | null;
  placeOfBirth: string | null;
  knownFor: string | null;
  photo: string | null;
  imdbId: string | null;
  credits: PersonCredit[];
}

// ---------- Kalender ----------

export interface CalEpisode {
  id: string;
  date: string;
  airTime: number | null;
  show: { title: string; tmdbId: number; ratingKey: string | null; poster: string | null; backdrop: string | null };
  season: number;
  episode: number;
  title: string;
  overview: string | null;
  still: string | null;
  ratingKey: string | null;
  premiere: boolean;
  source: 'library' | 'trakt' | 'list' | 'watching';
}

export interface CalMovie {
  id: string;
  date: string;
  kind: 'theatrical' | 'digital' | 'physical';
  tmdbId: number;
  title: string;
  year: number | null;
  overview: string | null;
  poster: string | null;
  backdrop: string | null;
  ratingKey: string | null;
  source: 'seerr' | 'trakt' | 'list';
}

export interface CalendarData {
  from: string;
  to: string;
  episodes: CalEpisode[];
  movies: CalMovie[];
  sources: { library: number; trakt: boolean; seerr: boolean; tmdb: boolean };
}

export const calendarApi = {
  get: (days = 30, past = 7) => call<CalendarData>(`/api/calendar?days=${days}&past=${past}`),
};

export interface ArtConfig { posterLang: 'nl' | 'en' | 'textless'; logoLang: 'nl' | 'en'; spatial: boolean; spatialLabels: boolean; canManage: boolean; spatialUrl?: string | null }
export interface ArtChoice { url: string; preview: string; lang: string | null; likes: number; source: 'fanart' | 'tmdb' }
export interface ArtOptions {
  posters: ArtChoice[];
  backdrops: ArtChoice[];
  logos: ArtChoice[];
  spatial: string | null;
  override: { poster?: string; backdrop?: string; logo?: string } | null;
  auto: { poster: string | null; backdrop: string | null; logo: string | null } | null;
  inPlex: boolean;
}

export const artApi = {
  config: () => call<ArtConfig>('/api/art/config'),
  saveConfig: (b: Partial<{ posterLang: ArtConfig['posterLang']; logoLang: ArtConfig['logoLang']; spatialUrl: string | null; spatialLabels: boolean }>) =>
    call<ArtConfig>('/api/art/config', { method: 'PUT', body: JSON.stringify(b) }),
  options: (type: 'movie' | 'tv', id: number) => call<ArtOptions>(`/api/art/options/${type}/${id}`),
  override: (type: 'movie' | 'tv', id: number, b: { poster?: string | null; backdrop?: string | null; logo?: string | null; reset?: boolean; plex?: boolean }) =>
    call<{ override: ArtOptions['override']; plex: 'done' | 'not_in_library' | 'failed' | 'skipped' }>(`/api/art/override/${type}/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
};

export const metaApi = {
  providers: () => call<{ canManage: boolean; providers: Record<Provider, { configured: boolean; ok: boolean | null; message: string | null }> }>('/api/providers'),
  saveProviders: (b: Partial<Record<Provider, string | null>>) =>
    call<{ results: Record<string, { ok: boolean; message?: string }>; providers: Record<Provider, { configured: boolean; ok: boolean | null; message: string | null }> }>('/api/providers', { method: 'PUT', body: JSON.stringify(b) }),
  meta: (type: 'movie' | 'tv', id: number) => call<Meta>(`/api/meta/${type}/${id}`),
  art: (source: 'tmdb' | 'fanart' | 'spatial', items: { type: 'movie' | 'tv'; tmdbId: number }[]) =>
    call<{ art: Record<string, { poster: string | null; backdrop: string | null; logo: string | null }> }>('/api/meta/art', { method: 'POST', body: JSON.stringify({ source, items }) }),
  inLibrary: (items: Pick<MiniItem, 'type' | 'tmdbId' | 'title' | 'year'>[]) =>
    call<{ found: Record<string, string | null> }>('/api/meta/in-library', { method: 'POST', body: JSON.stringify({ items }) }),
  trakt: () => call<{ configured: boolean; linked: boolean; username: string | null }>('/api/trakt'),
  traktDevice: () => call<{ deviceCode: string; userCode: string; verificationUrl: string; interval: number; expiresIn: number }>('/api/trakt/device', { method: 'POST', body: '{}' }),
  traktPoll: (deviceCode: string) => call<{ status: 'ok' | 'waiting' | 'expired' | 'denied'; linked: boolean; username: string | null }>('/api/trakt/device/poll', { method: 'POST', body: JSON.stringify({ deviceCode }) }),
  person: (id: number) => call<Person>(`/api/person/${id}`),
  personLookup: (name: string) => call<{ id: number | null }>(`/api/person-lookup?name=${encodeURIComponent(name)}`),
  traktUnlink: () => call<{ linked: false }>('/api/trakt', { method: 'DELETE' }),
  simkl: () => call<{ configured: boolean; linked: boolean; username: string | null }>('/api/simkl'),
  simklDevice: () => call<{ deviceCode: string; userCode: string; verificationUrl: string; interval: number; expiresIn: number }>('/api/simkl/device', { method: 'POST', body: '{}' }),
  simklPoll: (deviceCode: string) => call<{ status: 'ok' | 'waiting' | 'expired' | 'denied'; linked: boolean; username: string | null }>('/api/simkl/device/poll', { method: 'POST', body: JSON.stringify({ deviceCode }) }),
  simklUnlink: () => call<{ ok: true }>('/api/simkl', { method: 'DELETE' }),
};

// ---------- Indeling: hubs en widgets ----------

export type WidgetView = 'spotlight' | 'billboard' | 'poster' | 'netflix' | 'landscape' | 'wide' | 'top10' | 'wall';
export type WidgetShow = 'all' | 'owned' | 'missing';

export type WidgetSource =
  | { kind: 'plex'; list: 'continueWatching' | 'recentlyAdded' | 'unwatched' | 'genre' | 'watchlist' | 'collection' | 'playlist' | 'shelf'; library?: string; genre?: string; collection?: string; playlist?: string; name?: string; shelf?: ShelfKind }
  | { kind: 'tmdb'; list: 'trending_day' | 'trending_week' | 'popular' | 'now_playing' | 'upcoming' | 'top_rated' | 'on_the_air' | 'provider' | 'genre' | 'company' | 'network' | 'keyword' | 'person' | 'director' | 'collection' | 'list' | 'discover'; media: 'movie' | 'tv' | 'all'; providerId?: number; genreId?: number; sort?: 'popular' | 'new'; refId?: number; refName?: string; params?: Record<string, string>; sortBy?: string }
  | { kind: 'personal'; list: 'because' | 'actor' | 'director' | 'saga' | 'available' }
  | { kind: 'brands'; list: 'networks' | 'studios' | 'categories' }
  | { kind: 'addon'; list: 'catalog'; addonId: string; catalog: string; name?: string }
  | { kind: 'merge'; list: 'merge'; parts: WidgetSource[] }
  | { kind: 'tautulli'; list: 'activity' }
  | { kind: 'sportcenter'; list: 'ticker' | 'club' | 'table' | 'form'; team?: string; league?: string }
  | { kind: 'mdblist'; list: string; name?: string }
  | { kind: 'trakt'; list: 'trending' | 'popular' | 'anticipated' | 'boxoffice' | 'watchlist' | 'recommendations' | 'custom'; media: 'movie' | 'tv' | 'all'; url?: string; name?: string }
  | { kind: 'simkl'; list: 'trending' | 'plantowatch' | 'watching' | 'custom'; media: 'movies' | 'tv' | 'anime' | 'all'; period?: 'today' | 'week' | 'month'; url?: string; name?: string }
  | {
      kind: 'live';
      list: 'sport' | 'leagues' | 'nowtv';
      /** Competitielogo's: welke competities; Wedstrijden: alleen deze competities (leeg = alles) */
      leagues?: string[];
      /** Wedstrijden: alleen deze sporten (leeg = alles) */
      sports?: string[];
      /** Wedstrijden: alleen uitzendingen op Nederlandse zenders */
      nlOnly?: boolean;
      /** Wedstrijden: hoeveel uur vooruit */
      hours?: number;
      /** Nu op tv: vaste zenders ("account:stream"), in deze volgorde */
      channels?: string[];
      /** Nu op tv: hele categorieën ("account:categorie") */
      categories?: string[];
    }
  | { kind: 'seerr'; list: 'requests' }
  | { kind: 'calendar'; list: 'episodes' | 'movies' | 'all' };

export interface WidgetDef {
  id: string;
  title: string;
  source: WidgetSource;
  view: WidgetView;
  show: WidgetShow;
  limit: number;
  enabled: boolean;
  style?: 'default' | 'film' | 'border' | 'overlay';
  /** Schudden, minimale score, alleen digitaal uit, talen uitsluiten, gezien verbergen */
  filter?: RowFilter;
}

export interface RowFilter {
  shuffle?: 'daily' | 'weekly';
  minScore?: number;
  digitalOnly?: boolean;
  excludeLangs?: string[];
  hideWatched?: boolean;
}

export interface HubDef {
  id: string;
  name: string;
  widgets: WidgetDef[];
  /** Oud: Plex-bibliotheek waarboven deze hub ook staat; 'none' = nergens */
  library?: string;
  /** Bibliotheken en menu-pagina's waar deze hub bovenaan staat */
  pages?: string[];
  /** Als knop op Home tonen (standaard ja) */
  showOnHome?: boolean;
  /** Eigen hoes in de hub-balk */
  cover?: { image?: string; color?: string; icon?: string };
}

export interface LayoutDef {
  v: 1;
  preset?: string;
  hubs: HubDef[];
}

export interface ExtItem {
  /** Voor bronnen uit addons (Stremio-id) en Verder kijken van Trakt */
  imdb?: string | null;
  season?: number;
  episode?: number;
  epTitle?: string;
  type: 'movie' | 'tv';
  tmdbId: number;
  title: string;
  year: number | null;
  overview: string | null;
  poster: string | null;
  backdrop: string | null;
  ratingKey: string | null;
  requestStatus?: 'requested' | 'processing' | 'partial' | 'available' | null;
  releaseDate?: string | null;
  /** Kwaliteit uit Plex als de titel in de bibliotheek staat */
  flags?: { label: string; kind: 'res' | 'hdr' | 'audio' }[];
  /** Score 0-10 (Plex, anders TMDB) */
  score?: number | null;
  /** Rij "Nu beschikbaar": in Plex of met een addon-bron die meteen speelt */
  available?: 'plex' | 'addon';
}

export interface Brands {
  networks: { id: number; name: string; logo: string }[];
  studios: { id: number; name: string; logo: string }[];
  categories: { id: number; name: string; media: 'movie' | 'tv'; image: string | null }[];
}

export interface WidgetOptions {
  available: { tmdb: boolean; mdblist: boolean; trakt: boolean; traktLinked: boolean; simkl?: boolean; simklLinked?: boolean };
  genres: { movie: { id: number; name: string }[]; tv: { id: number; name: string }[] };
  providers: { id: number; name: string; logo: string | null }[];
  mdblistTop: { id: string; name: string; user: string; items: number }[];
}

export const layoutApi = {
  current: () => call<{ layout: LayoutDef; scope: 'profile' | 'default'; canEditDefault: boolean; presets: { id: string; name: string }[] }>('/api/layout'),
  get: (scope: 'default' | 'profile') => call<{ layout: LayoutDef | null }>(`/api/layout/${scope}`),
  save: (scope: 'default' | 'profile', layout: LayoutDef) => call<{ layout: LayoutDef }>(`/api/layout/${scope}`, { method: 'PUT', body: JSON.stringify(layout) }),
  resetProfile: () => call<{ layout: LayoutDef }>('/api/layout/profile', { method: 'DELETE' }),
  preset: (id: string) => call<{ layout: LayoutDef }>(`/api/layout/preset/${id}`),
  options: () => call<WidgetOptions>('/api/widgets/options'),
  importCollections: (input: { json?: string; url?: string }) =>
    call<{ hubs: HubDef[]; folders: number; skipped: string[]; name: string }>('/api/layout/import-collections', { method: 'POST', body: JSON.stringify(input) }),
  items: (source: WidgetSource, limit: number, show: WidgetShow, offset?: number, filter?: RowFilter) =>
    call<{ items: ExtItem[]; heading?: string; more?: boolean }>('/api/widgets/items', { method: 'POST', body: JSON.stringify({ source, limit, show, offset, filter }) }),
  brands: () => call<Brands>('/api/brands'),
  brand: (kind: 'company' | 'network' | 'keyword', id: number) => call<{ id: number; name: string; logo: string | null }>(`/api/brands/${kind}/${id}`),
};

export type ThemeScope = 'builtin' | 'shared' | 'personal';

export interface Theme {
  id: string;
  name: string;
  author?: string;
  version?: number;
  /** Waar de skin vandaan komt (alleen in antwoorden van de server) */
  scope?: ThemeScope;
  tokens: {
    /** Vergroting van de hele interface (0.8–1.3) */
    scale?: number;
    color: Record<string, string>;
    font: { display?: string; body?: string; googleFonts?: string };
    radius: Record<string, number>;
    focus?: { style?: 'ring' | 'glow' | 'lift'; scale?: number };
  };
  layout: {
    nav: 'top' | 'left' | 'sidebar';
    home: { type: 'hero' | 'row' | 'carousel' | 'grid'; title?: string; source: string; card?: 'poster' | 'landscape' | 'wide' }[];
  };
  components: {
    posterCard?: string;
    posterBadges?: 'auto' | 'always' | 'never';
    artwork?: 'poster' | 'landscape';
    /** 'focus': achtergrond wisselt mee met de geselecteerde titel (Arctic Fuse) */
    backdrop?: 'focus' | 'none';
    /** Clearlogo in plaats van tekst in de spotlight en op infopagina's */
    logos?: boolean;
    cardTitles?: 'show' | 'hide';
    density?: 'compact' | 'normal' | 'large';
    heroHeight?: 'compact' | 'normal' | 'tall';
    navStyle?: 'pill' | 'underline' | 'plain';
    glass?: boolean;
    /** Klok in het menu */
    clock?: boolean;
    seasonal?: 'auto' | 'off';
    titleLayout?: 'classic' | 'cinematic';
    followColor?: boolean;
    reduceMotion?: boolean;
    disc?: boolean;
    episodeView?: 'list' | 'cards' | 'cinema';
    mediaFlags?: 'off' | 'basic' | 'full';
    mediaFlagsPos?: 'tl' | 'tr' | 'bl' | 'br';
  };
}

export interface ThemeListItem {
  id: string;
  name: string;
  scope: ThemeScope;
  editable: boolean;
}

export const themeApi = {
  list: () => call<{ themes: ThemeListItem[]; canShare: boolean }>('/api/themes'),
  get: (id: string) => call<Theme>(`/api/themes/${encodeURIComponent(id)}`),
  save: (theme: Theme, scope: 'personal' | 'shared', id?: string) =>
    call<{ theme: Theme }>('/api/themes', { method: 'POST', body: JSON.stringify({ theme, scope, id }) }),
  remove: (id: string) => call<{ ok: true }>(`/api/themes/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  choice: () => call<{ themeId: string | null }>('/api/theme-choice'),
  setChoice: (themeId: string) => call<{ themeId: string }>('/api/theme-choice', { method: 'PUT', body: JSON.stringify({ themeId }) }),
};

// ---------- Kinderslot ----------

export interface Bedtime { from: string; to: string }

export const localListApi = {
  state: (q: { type?: 'movie' | 'tv'; tmdbId?: number; imdb?: string }) =>
    call<{ onList: boolean; unknown?: boolean }>(`/api/watchlist/local/state?${new URLSearchParams(Object.entries(q).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString()}`),
  set: (b: { type?: 'movie' | 'tv'; tmdbId?: number; imdb?: string; title?: string; year?: number | null; on: boolean }) =>
    call<{ onList: boolean }>('/api/watchlist/local', { method: 'PUT', body: JSON.stringify(b) }),
};

export const watchlistApi = {
  state: (guid: string) => call<{ onList: boolean; unknown?: boolean }>(`/api/watchlist/state?guid=${encodeURIComponent(guid)}`),
  set: (guid: string, on: boolean) => call<{ onList: boolean }>('/api/watchlist', { method: 'PUT', body: JSON.stringify({ guid, on }) }),
};

export const parentalApi = {
  status: () => call<{ pinSet: boolean; locked: boolean; bedtime: Bedtime | null; unlockedUntil: number | null }>('/api/parental/status'),
  get: () => call<{ pinSet: boolean; bedtime: Record<string, Bedtime> }>('/api/parental'),
  save: (b: { pin?: string | null; currentPin?: string; bedtime?: Record<string, Bedtime | null> }) =>
    call<{ pinSet: boolean; bedtime: Record<string, Bedtime> }>('/api/parental', { method: 'PUT', body: JSON.stringify(b) }),
  unlock: (pin: string) => call<{ ok: true; unlockedUntil?: number }>('/api/parental/unlock', { method: 'POST', body: JSON.stringify({ pin }) }),
};

// ---------- Filmreeksen ----------

export interface SagaPart { tmdbId: number; title: string; year: number | null; poster: string | null; backdrop: string | null; released: boolean; ratingKey: string | null; watched: boolean; progress: number }
export interface SagaSummary { id: number; name: string; overview: string | null; poster: string | null; backdrop: string | null; logo?: string | null; total: number; have: number; seen: number; mosaic: string[]; next: SagaPart | null }
export interface Saga extends SagaSummary { parts: SagaPart[] }

export const collectionsApi = {
  list: () => call<{ sagas: SagaSummary[]; building: { done: number; total: number } | null }>('/api/collections'),
  get: (id: number) => call<{ saga: Saga }>(`/api/collections/${id}`),
};

// ---------- IntroDB (intro, samenvatting, aftiteling) ----------

export interface Segment { start: number; end: number }
export interface Segments { intro: Segment | null; recap: Segment | null; outro: Segment | null; postCredits: Segment | null; source: 'introdb' | 'aniskip' | null }

export const introdbApi = {
  status: () => call<{ configured: boolean }>('/api/introdb'),
  save: (key: string | null) => call<{ configured: boolean }>('/api/introdb', { method: 'PUT', body: JSON.stringify({ key }) }),
  segments: (ratingKey: string) => call<Segments>(`/api/segments/${ratingKey}`),
  submit: (ratingKey: string, type: 'intro' | 'recap' | 'outro', start: number, end: number) =>
    call<{ ok: true }>(`/api/segments/${ratingKey}/submit`, { method: 'POST', body: JSON.stringify({ type, start, end }) }),
};

// ---------- Uitnodigingen en onboarding ----------

export interface InviteAddon { id: string; name: string; description: string | null; icon: string | null; kind: 'video' | 'music'; mode: 'share' | 'recommend'; setupUrl: string | null }

export interface OfferPick { id: string; mode: 'share' | 'recommend'; setupUrl: string | null }

export interface InviteInfo {
  id: string;
  code: string;
  label: string;
  createdAt: number;
  expiresAt: number | null;
  maxUses: number | null;
  uses: number;
  template: string;
  needsApproval: boolean;
  share: boolean;
  sectionIds: string[];
  addons: InviteAddon[];
  revoked: boolean;
  link: string;
  usable: boolean;
  access?: 'plex' | 'addons' | 'choice';
  sources?: InviteSources;
}

export interface MemberInfo {
  id: string;
  name: string;
  email: string;
  inviteId: string;
  createdAt: number;
  status: 'registered' | 'pending' | 'active' | 'blocked';
  accountId: number | null;
  plexUsername: string | null;
  share: 'none' | 'shared' | 'failed';
  shareError: string | null;
  kind?: 'plex' | 'local';
  plexLinked?: string | null;
  profiles?: number | null;
  lastLogin?: number | null;
}

export interface InviteAdmin {
  required: boolean;
  envOff: boolean;
  templates: { id: string; name: string; sources?: InviteSources }[];
  sections: { id: string; key: string; title: string; type: string }[] | null;
  invites: InviteInfo[];
  members: MemberInfo[];
}

export interface NewInvite {
  label: string;
  days: number | null;
  maxUses: number | null;
  template: string;
  needsApproval: boolean;
  share: boolean;
  sectionIds: string[];
  addons: OfferPick[];
  access: 'plex' | 'addons' | 'choice';
  sources: InviteSources;
}

export interface InviteSources { addons: boolean; debrid: boolean; usenet: boolean }

export const inviteApi = {
  publicInfo: (code: string) => call<{ valid: true; label: string; expiresAt: number | null; access: 'plex' | 'addons' | 'choice'; needsApproval: boolean }>(`/api/invites/public/${encodeURIComponent(code)}`),
  registerLocal: (b: { code: string; name: string; email: string; password: string }) =>
    call<{ ok: true; name: string; pending?: boolean; loggedIn?: boolean }>('/api/invites/register-local', { method: 'POST', body: JSON.stringify(b) }),
  resetPassword: (id: string) => call<{ password: string }>(`/api/admin/members/${id}/reset-password`, { method: 'POST' }),
  register: (b: { code: string; name: string; email: string }) => call<{ ok: true; name: string }>('/api/invites/register', { method: 'POST', body: JSON.stringify(b) }),
  admin: () => call<InviteAdmin>('/api/admin/invites'),
  ownAddons: () => call<{ addons: { id: string; name: string; kind: 'video' | 'music'; icon: string | null; setupUrl: string | null }[] }>('/api/admin/invites/addons'),
  setRequired: (required: boolean) => call<{ required: boolean; envOff: boolean }>('/api/admin/invites/settings', { method: 'PUT', body: JSON.stringify({ required }) }),
  create: (b: NewInvite) => call<{ invite: InviteInfo }>('/api/admin/invites', { method: 'POST', body: JSON.stringify(b) }),
  revoke: (id: string) => call<{ ok: true }>(`/api/admin/invites/${id}`, { method: 'DELETE' }),
  approve: (id: string) => call<{ member: MemberInfo }>(`/api/admin/members/${id}/approve`, { method: 'POST' }),
  block: (id: string) => call<{ member: MemberInfo }>(`/api/admin/members/${id}/block`, { method: 'POST' }),
  removeMember: (id: string) => call<{ ok: true }>(`/api/admin/members/${id}`, { method: 'DELETE' }),
};

export const onboardingApi = {
  info: () => call<{ needed: boolean; name: string; owner: boolean; offeredAddons: InviteAddon[] }>('/api/onboarding'),
  addAddon: (id: string) => call<{ addon: unknown }>('/api/onboarding/addon', { method: 'POST', body: JSON.stringify({ id }) }),
  done: () => call<{ ok: true }>('/api/onboarding/done', { method: 'POST' }),
};

// ---------- Kijkgeschiedenis (Trakt/SIMKL) ----------

export interface SyncStatus {
  trakt: { configured: boolean; linked: boolean; username: string | null };
  simkl: { configured: boolean; linked: boolean; username: string | null; canWrite?: boolean };
  import: { state: 'running' | 'done' | 'error'; movies: number; episodes: number; message?: string; at: number } | null;
  back: { lastRun?: number; lastResult?: string } | null;
  pending?: number;
}

export const syncApi = {
  status: () => call<SyncStatus>('/api/sync'),
  /** Na "markeer als (niet) bekeken" in Plex: ook op Trakt/SIMKL (stil, als het aan staat) */
  watched: (ratingKey: string, watched: boolean) => call<{ ok?: boolean; skipped?: boolean }>('/api/sync/watched', { method: 'POST', body: JSON.stringify({ ratingKey, watched }) }).catch(() => undefined),
  import: (b: { trakt: boolean; simkl: boolean }) => call<{ import: SyncStatus['import'] }>('/api/sync/import', { method: 'POST', body: JSON.stringify(b) }),
  pull: () => call<{ back: SyncStatus['back'] }>('/api/sync/pull', { method: 'POST' }),
};

// ---------- Overzicht voor de beheerder ----------

export interface OverviewNow {
  kind: 'plex' | 'addon' | 'live';
  who: string;
  title: string;
  sub: string | null;
  device: string;
  mode: string;
  quality: string | null;
  progress: number | null;
  paused: boolean;
  image: string | null;
  plexThumb: string | null;
}

export interface OverviewSample {
  at: number;
  cpu: number | null;
  memUsed: number;
  memTotal: number;
  rxMbps: number | null;
  txMbps: number | null;
}

export interface Overview {
  version: string;
  uptimeSec: number;
  now: OverviewNow[];
  nowError: string | null;
  health: { name: string; state: 'ok' | 'warn' | 'bad' | 'off'; detail: string }[];
  activity: { day: string; plex: number; addon: number; live: number }[];
  plexHistory: boolean;
  totals: { plays30: number; playsPrev30: number; viewers30: number };
  split: { plex: number; addon: number; live: number };
  server: { samples: OverviewSample[]; cores: number; memory: { used: number; total: number }; disk: { free: number; total: number } | null };
  rows: { title: string; n: number }[];
}

export const adminApi = {
  overview: () => call<Overview>('/api/admin/overview'),
};

/** Een titel geopend vanuit een rij op Home (telt mee in "Beste rijen" van het Overzicht). */
export function rowOpened(title: string): void {
  void call('/api/stats/row', { method: 'POST', body: JSON.stringify({ title }) }).catch(() => undefined);
}

// ---------- Debrid (eigen sleutel) ----------

export interface DebridProvider { id: 'torbox' | 'realdebrid' | 'alldebrid' | 'premiumize'; name: string; linked: boolean; ok: boolean; premium: boolean; account: string | null; expires: number | null; message: string | null }
export interface DebridState { allowed?: boolean; providers: DebridProvider[]; addon: boolean; warning?: string | null }

export const debridApi = {
  get: () => call<DebridState & { allowed: boolean }>('/api/debrid'),
  link: (provider: DebridProvider['id'], key: string) => call<DebridState>(`/api/debrid/${provider}`, { method: 'PUT', body: JSON.stringify({ key }) }),
  unlink: (provider: DebridProvider['id']) => call<DebridState>(`/api/debrid/${provider}`, { method: 'DELETE' }),
  refresh: () => call<DebridState>('/api/debrid/refresh', { method: 'POST' }),
  addon: (on: boolean) => call<DebridState>('/api/debrid/addon', { method: 'PUT', body: JSON.stringify({ on }) }),
};

// ---------- Smaakprofiel ----------

export interface Taste {
  plays: number;
  titles: number;
  genres: { name: string; pct: number }[];
  habits: { days: string[]; partOfDay: string | null; moviesPct: number; episodesPct: number; perEvening: number | null; addonPct: number };
  top: { title: string; plays: number }[];
  plexAvailable: boolean;
}

export const tasteApi = {
  mine: () => call<Taste>('/api/taste'),
  of: (uuid: string, name: string, admin: boolean) => call<Taste>(`/api/admin/taste/${encodeURIComponent(uuid)}?${new URLSearchParams({ name, admin: admin ? '1' : '0' })}`),
};

// ---------- Ondertitels (OpenSubtitles) ----------

export interface SubResult { fileId: number; lang: string; release: string; downloads: number; hearingImpaired: boolean; trusted: boolean; fps: number | null; match: number; source?: string }

export const subsApi = {
  search: (q: { id: string; tmdb?: number; type?: string; release?: string; langs: string[] }) => {
    const p = new URLSearchParams({ id: q.id, langs: q.langs.join(',') });
    if (q.tmdb) p.set('tmdb', String(q.tmdb));
    if (q.type) p.set('type', q.type);
    if (q.release) p.set('release', q.release.slice(0, 300));
    return call<{ configured: boolean; results: SubResult[] }>(`/api/subs/search?${p}`);
  },
  fileUrl: (fileId: number) => `/api/subs/file/${fileId}.vtt`,
};
